// src/data/benchmarkBandsClient.js
//
// ADMIN-BENCHMARKS-S1: Client for benchmark band configuration endpoints.
//
//   GET  /api/admin/benchmark-bands?metric_key=  — fetch bands for a metric
//   PUT  /api/admin/benchmark-bands/:metricKey   — replace full set (admin)
//   POST /api/admin/benchmark-bands/:metricKey/reset — reset to defaults (admin)

import {
  AuthError,
  NetworkError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { log } from "../utils/logger.js";

async function jsonRequest(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    });
  } catch (err) {
    log.error("benchmarkBandsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("benchmarkBandsClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * Fetch all bands for a given metric key.
 * @param {string} metricKey
 * @returns {Promise<{ bands: Array }>}
 */
export async function getBenchmarkBands(metricKey) {
  return jsonRequest(
    `/api/admin/benchmark-bands?metric_key=${encodeURIComponent(metricKey)}`,
  );
}

/**
 * Replace the full band set for a metric.
 * @param {string} metricKey
 * @param {Array<{ band_order, max_value, color, label }>} bands
 * @returns {Promise<{ bands: Array }>}
 */
export async function putBenchmarkBands(metricKey, bands) {
  return jsonRequest(`/api/admin/benchmark-bands/${encodeURIComponent(metricKey)}`, {
    method: "PUT",
    body: JSON.stringify({ bands }),
  });
}

/**
 * Reset bands for a metric to built-in defaults.
 * @param {string} metricKey
 * @returns {Promise<{ bands: Array }>}
 */
export async function resetBenchmarkBands(metricKey) {
  return jsonRequest(
    `/api/admin/benchmark-bands/${encodeURIComponent(metricKey)}/reset`,
    { method: "POST", body: JSON.stringify({}) },
  );
}
